import { TrashIcon } from '@heroicons/react/24/outline';
import { Column, Row } from '@tanstack/react-table';
import { useState } from 'react';

import { IconButton } from '@/components/core/IconButton';
import { LOCAL_STORAGE } from '@/constants.ts';
import ConfirmationModal from '@/dialogs/confirmation-modals/ConfirmationModal.tsx';
import { useLocalStorage } from '@/hooks/useLocalStorage.ts';
import { useTranslation } from '@/hooks/useTranslation.tsx';
import { useUser } from '@/hooks/useUser.tsx';

interface CollectionActionCell<TData> {
    row: Row<TData>;
    column: Column<TData>;
    remove: (rowIndex: number) => void,
}

const CollectionActionCell = <TData,>({ remove, row }: CollectionActionCell<TData>) => {
    const { t } = useTranslation();
    const { user } = useUser();
    const [showRemoveDialog, setShowRemoveDialog] = useState(false);

    const [isDeleteConfirmationDisabled] =
        useLocalStorage<boolean>(
            `${LOCAL_STORAGE.DISABLE_DELETE_CONFIRMATION}${user?.id}`,
            false,
        );

    const handleRemove = (closeDialog = true) => {
        remove(row.index);
        if (closeDialog) setShowRemoveDialog(false);
    };

    const handleCancel = () => {
        setShowRemoveDialog(false);
    };

    return (
        <div className="tw-flex tw-flex-row-reverse tw-py-0">
            <div id={`row${row.id}-actions`}>
                <IconButton
                    type="button"
                    data-testid={`delete-entry-${row.id}`}
                    className="tw-icon-primary"
                    icon={TrashIcon}
                    size="md"
                    variant="destructive"
                    onClick={() => isDeleteConfirmationDisabled? handleRemove(false): setShowRemoveDialog(true)}
                    title={t('ACTION.DELETE')}
                />
                <ConfirmationModal
                    title='GROUP_DELETE_ENTRY'
                    subTitle='EDITOR.DELETE_CONFIRMATION_TEXT.ROW'
                    discardBtnText = 'ACTION.DELETE'
                    closeBtnText ='CANCEL'
                    btnVariant="destructive"
                    show={showRemoveDialog}
                    onSubmit={handleRemove}
                    onClose={handleCancel}
                />
            </div>
        </div>
    );
};

export { CollectionActionCell };
